import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Download, FileCheck2, ShieldCheck, ExternalLink } from "lucide-react";
import { STANDARDS, FEATURE_GROUPS } from "@/constants/compliance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public audit-readiness pack request dialog.
 * Captures name/email/company/role + selected standard, posts to /api/audit-pack/request,
 * shows a download link to the auto-generated personalized PDF.
 */
export default function AuditPackDialog({ open, onClose, defaultStandard }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", standard_key: defaultStandard || "iso9001", notes: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ name: "", email: "", company: "", role: "", standard_key: defaultStandard || "iso9001", notes: "" });
      setResult(null);
    }
  }, [open, defaultStandard]);

  const selectedStd = STANDARDS.find((s) => s.key === form.standard_key) || STANDARDS[0];

  // Snapshot the feature → clauses mapping for the chosen standard so the backend PDF matches the on-screen content exactly.
  const items = useMemo(() => {
    const out = [];
    FEATURE_GROUPS.forEach((g) => {
      g.features.forEach((f) => {
        const c = f.citations.find((c) => c.std === selectedStd.key);
        if (!c) return;
        out.push({ group: g.group, title: f.title, summary: f.summary, clauses: c.clauses });
      });
    });
    return out;
  }, [selectedStd.key]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.email.trim()) return toast.error("Email is required");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/audit-pack/request`, {
        ...form,
        standard_name: selectedStd.name,
        items,
      });
      setResult(r.data);
      toast.success("Pack generated · check the download link below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const downloadHref = result ? `${process.env.REACT_APP_BACKEND_URL}${result.download_url}` : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl border-[#1a1a1a]/15 max-h-[90vh] overflow-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#ff4500]" /> Audit-readiness pack
          </DialogTitle>
          <DialogDescription className="sr-only">Generate a personalised compliance-coverage PDF for your target standard.</DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <p className="text-[12px] text-[#1a1a1a]/75">
              We&apos;ll auto-generate a customised PDF showing every clause in your chosen standard alongside the specific hubCMD feature that satisfies it. No charge, no card required.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Your name" tid="pack-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Carlos Rivera" />
              <Field label="Work email" tid="pack-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="you@company.com" />
              <Field label="Company" tid="pack-company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} placeholder="Acme Manufacturing" />
              <Field label="Your role" tid="pack-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} placeholder="QA Manager" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Target standard</label>
              <select data-testid="pack-standard" value={form.standard_key}
                onChange={(e) => setForm({ ...form, standard_key: e.target.value })}
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
                {STANDARDS.map((s) => (<option key={s.key} value={s.key}>{s.name} — {s.short}</option>))}
              </select>
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mt-1">
                {items.length} hubCMD feature{items.length === 1 ? "" : "s"} cite this standard
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Anything specific?</label>
              <textarea data-testid="pack-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional — e.g. 'preparing for a SQF recertification in Q3'"
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm h-16" />
            </div>
            <div className="bg-gradient-to-br from-[#fff1ea] to-white border border-[#ff4500]/20 rounded-xl p-3 text-[12px]">
              <b className="text-[#ff4500]">Heads up.</b> Your details get a single mock-outbound email log entry on our side so we can follow up about <b>TruPath Advisory</b> consulting (Lean / CI / 3rd-party audit programs). No spam, opt-out anytime.
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="pack-success">
            <div className="bg-[#1a8754]/10 border border-[#1a8754]/30 rounded-xl p-4 text-center">
              <FileCheck2 className="w-10 h-10 text-[#1a8754] mx-auto" />
              <div className="font-display font-black text-xl mt-2">Your pack is ready</div>
              <div className="text-[12px] text-[#1a1a1a]/75 mt-1">Personalized PDF for {result.request.standard_name}.</div>
              <a href={downloadHref} target="_blank" rel="noopener noreferrer"
                data-testid="pack-download"
                className="mt-4 inline-flex btn-pill btn-pill-primary">
                <Download className="w-4 h-4" /> Download PDF
              </a>
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mt-2">Pack ID · {result.request.display_id}</div>
            </div>
            <a href="https://trupathadvisory.com" target="_blank" rel="noopener noreferrer"
              data-testid="pack-trupath"
              className="block bg-[#1a1a1a] text-white rounded-xl p-4 hover:bg-[#0e0e0e] transition-colors">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#ff5f15]">// Next step</div>
              <div className="font-display font-bold text-base mt-1">Want a Lean / audit specialist on your side?</div>
              <div className="text-[12px] text-white/75 mt-1">TruPath Advisory builds Lean &amp; 3rd-party-audit programs end-to-end — and built hubCMD itself.</div>
              <div className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-[#ff5f15]">
                Visit TruPathAdvisory.com <ExternalLink className="w-3 h-3" />
              </div>
            </a>
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <button data-testid="pack-submit" onClick={submit} disabled={busy} className="btn-pill btn-pill-primary disabled:opacity-50">
              <Download className="w-3.5 h-3.5" /> {busy ? "Generating…" : "Generate my pack"}
            </button>
          ) : (
            <button data-testid="pack-close" onClick={onClose} className="btn-pill btn-pill-secondary">Done</button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, tid, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">{label}</label>
      <input data-testid={tid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1a1a1a]" />
    </div>
  );
}
